import { apiClient, ensureCsrfCookie } from '../../api/client';
import type { ScreenWords } from '../clients/api';
import type { FilialPlashka, Razbor } from '../obrashcheniya/api';
import type { Liniya } from '../obrashcheniya/components/VyborLiniy';
import type { Istochnik, Proekt } from '../obrashcheniya/components/VyborProekta';
import type { ColorKey } from '../zapis/plashka8/reestr';
import type { ZapisRow } from '../zapis/api';
import type { DealReference } from './amo-api';

/**
 * ДВЕ ДОСКИ — лиды и записи (`s635`, макеты `s625`).
 *
 * ⛔ НИ ОДНОГО РАСЧЁТА НА ФРОНТЕ. Колонки, их порядок, краска, слова, число в
 * колонке и фильтры приезжают ПОСЧИТАННЫМИ сервером (`EtapDoski`,
 * `KolonkiDosok`, словарь). Второго списка колонок здесь нет: он разошёлся бы
 * с сервером молча (ТЗ v3 §4.3 «экран колонку не считает»).
 */

export interface Metka {
  kod: string;
  slovo: string;
  ton?: 'trevoga' | 'tiho';
}

// ── Доска лидов ──────────────────────────────────────────────────────────────

export interface KartochkaLidaNaDoske {
  id: number;
  reference?: { kind: 'ad_leads' | 'amo_native_deals'; local_id: number | null; original_amo_id: number | null };
  customer_id: number | null;
  /** Маска краями; имя — только если оно реально известно. */
  telefon: string | null;
  kto_eto: string | null;
  imya: string | null;
  istochnik: { kod: string; slovo: string };
  filial: FilialPlashka;
  fraza: string | null;
  metki: Metka[];
  derzhit_user_id: number | null;
  /** `s722`: версия лида, с которой показана плитка; бросок возвращает её серверу. Прежний сервер её не шлёт. */
  versiya?: string | null;
  zapis: { id: number; kogda: string } | null;
  /** `s682` П6 — строки плашки словами сервера (свод `PlashkaLida`). */
  otkuda?: string | null;
  prishla?: string | null;
  obzvon?: string | null;
  shag?: string | null;
  /** `s707` — три блока плашки (свод `PlashkaLida::plashkiPachkoy`); нет — прежний вид строк. */
  plashka?: PlashkaLidaBloki | null;
}

/** Строка середины плашки: звонок · действие робота · ожидание автоматики · задача. */
export interface StrokaPlashki {
  vid: 'zvonok' | 'robot' | 'ozhidanie' | 'zadacha';
  kogda: string | null;
  /** Полная фраза и короткая — для узкой плашки. */
  tekst: string;
  korotko: string;
  /** Сотрудник своего звонка или задачи; общего «ответственного» нет. */
  kto: string | null;
  ton: 'obychno' | 'trevoga';
  podskazka: string;
}

export interface PlashkaLidaBloki {
  /** Пояснение ★ «уже была на визите до этой заявки»; нет визита — `null`. */
  zvezda: string | null;
  /** Мессенджер, который просила клиентка; `kanal = null` — просила переписку без канала. */
  svyaz: { kanal: string | null; podskazka: string } | null;
  telefon_korotko?: string | null;
  stroki: StrokaPlashki[];
  priznaki?: Array<{ kod: string; slovo: string; ton: string; podskazka: string }>;
  niz: { domen: string | null; forma: string | null; forma_korotko: string | null; kampaniya: string | null; kampaniya_korotko?: string | null; podskazka: string } | null;
  prishla: string | null;
  /** `s772`: дата заявки числом («08.10») — в верхней строке плашки. */
  data?: string | null;
  /** `s772`: «Похоже на робота: причины» — на плашке идёт первым тревожным признаком (`priznaki`, код `robot`). */
  robot?: string | null;
  /** `s772`: лид недожатой формы — на плашке признак `nedozhataya` заметным тоном `vnimanie`. */
  nedozhataya?: boolean;
  /** `s775`: робот прошёл все попытки недозвона и не дозвонился — плашка в тонкой рамке, признак первым. */
  robot_ne_dozvonilsya?: boolean;
}

export interface KolonkaLidov {
  kod: string;
  slovo: string;
  cvet: string;
  chislo: number;
  hodom: boolean;
  /** `s674`: колонка открытых лидов — целиком «В обзвон (N)». */
  obzvon?: boolean;
  /** `s702`: заявки колонки, допущенные сервером в обзвон (число на кнопке и то, что она отправит). */
  obzvon_ids?: number[];
  /** `s707`: колонка исхода — на доске лидов свёрнута полосой, раскрывается значком. */
  svernuta?: boolean;
  kartochki: KartochkaLidaNaDoske[];
}

export interface Prichina {
  code: string;
  label: string;
}

/** `s731`: совпадение поиска вне доски. Телефона здесь нет намеренно; имя — уже под маской сервера. */
export interface NaydenoVne {
  id: number;
  filial_id: number | null;
  filial: string | null;
  drugoy_filial: boolean;
  imya: string | null;
  etap: string | null;
  otkryt: boolean;
  prishla: string | null;
}

export interface DoskaLidovOtvet {
  filial_id: number | null;
  /** `[s635-filtr-linii]` */
  linii: Liniya[];
  vybrany_linii: number[];
  /** `s675`: проекты филиала шапки (0 — «Весь филиал»); пусто — фильтра нет. */
  proekty: Proekt[];
  vybrany_proekty: number[];
  /** `s675`: источник — не колонка, а фильтр. */
  istochniki: Istochnik[];
  istochnik: string | null;
  moi: boolean;
  poisk: string | null;
  /** `s731`: лиды, которые поиск нашёл, а эта доска не показала — другой филиал, старше окна, сняты с доски. */
  naydeno_vne?: NaydenoVne[];
  /** `s731`: новые заявки в других филиалах — подсказка на доске выбранного филиала. */
  novye_v_drugih?: Array<{ filial_id: number; filial: string; chislo: number }>;
  filialy: FilialPlashka[];
  schyotchiki: {
    novyh: number;
    novyh_display: string;
    pora_perezvonit: number;
    pora_perezvonit_display: string;
  };
  kolonki: KolonkaLidov[];
  /** Колонка доски → код колонки двери `peretashchit`. */
  hody: Record<string, string>;
  /** `s705`: колонки, которые ставит рука, → что спросить перед ходом (час, причина) или `null`. */
  ruchnye?: Record<string, NuzhnoDlyaStatusa | null>;
  /** `s705`: причины отказа клиентки — для броска в «Отменён». */
  prichiny_otkaza_lida?: Array<{ id: number; label: string }>;
  prichiny_otbrosa: Prichina[];
  prichiny_otkladyvaniya: Prichina[];
  provereno_v: string;
  words: ScreenWords;
}

export interface DoskaLidovFiltr {
  filial_id: number | null;
  moi: boolean;
  poisk: string;
  linii: number[];
  proekty: number[];
  istochnik: string | null;
}

export async function fetchDoskaLidov(f: DoskaLidovFiltr): Promise<DoskaLidovOtvet> {
  const { data } = await apiClient.get<DoskaLidovOtvet>('/doska/lidy', {
    params: {
      filial_id: f.filial_id ?? undefined,
      moi: f.moi ? 1 : undefined,
      poisk: f.poisk.trim() !== '' ? f.poisk.trim() : undefined,
      linii: f.linii.length > 0 ? f.linii : undefined,
      proekty: f.proekty.length > 0 ? f.proekty : undefined,
      istochnik: f.istochnik ?? undefined,
    },
  });

  return data;
}

// ── Карточка лида (добор разбора обращения) ─────────────────────────────────

export interface ZapisLida {
  /** `s722`: оплата записи — у внесённой брони повторной ссылки нет; назначение и сумма будущей ссылки названы. */
  oplata?: { bron_vnesena: boolean; fakt: string | null; ssylka_mozhno: boolean; naznachenie: string | null };
  id: number;
  data: string | null;
  vremya: string | null;
  master: string | null;
  usluga: string | null;
  status: string | null;
  bron: string | null;
  prosrochena: boolean;
  /** `s705`: эта запись в журнале её кабинета YClients (истина по записям — УС). */
  ssylka_us?: string | null;
  /** Когда наша копия записи последний раз менялась — словами сервера. */
  obnovleno?: string | null;
  /** Открыта ли смотрящему наша карточка записи (раздел «Запись»). */
  nasha_kartochka?: boolean;
}

/** `s705`: что экран спрашивает перед ручным ходом или куда ведёт вместо него. */
export type NuzhnoDlyaStatusa = 'chas' | 'prichina_otkaza' | 'prichina_tresh' | 'zapisat' | 'otkryt_zapis' | 'svyazat_zapis';

/** `s705`: строка выбора статуса в карточке лида — всё решил сервер (`RuchnoyStatusLida`). */
export interface StatusLidaDlyaVybora {
  kod: string;
  slovo: string;
  cvet: string;
  tekushchiy: boolean;
  /** Ставит ли эту колонку рука; нет — `pochemu` говорит, что сделать. */
  rukoy: boolean;
  nuzhno: NuzhnoDlyaStatusa | null;
  pochemu: string | null;
  zapis_id: number | null;
}

/** `s722`: пометка администратора или причина отказа словами из раздела «Лиды с форм». */
export interface PometkaSFormy {
  id: number;
  vid: 'pometka' | 'prichina_otkaza';
  vid_slovo: string;
  tekst: string;
  avtor: string;
  kogda: string;
  status: string;
  zayavka_id: number;
}

export interface BlokDoski {
  /** `s722`: пометки «Лидов с форм» с автором и датой; `null` — пометок нет. */
  pometki_form?: { zagolovok: string; stroki: PometkaSFormy[] } | null;
  /** `s772`: «Похоже на робота: причины» — фраза целиком из свода «Лидов с форм»; чистый лид — `null`. */
  robot?: string | null;
  /** `s772` (владелец 11.10.2026): лид недожатой формы — фраза пометки; заявка есть — `null`. */
  nedozhataya?: string | null;
  /**
   * `s775` (владелец 11.10.2026): робот прошёл все попытки недозвона и не дозвонился — панель решения
   * сверху карточки. Слова кнопок и ход «в треш» называет сервер; `null` — решать нечего.
   */
  robot_ne_dozvonilsya?: {
    tekst: string;
    knopki: { zanovo: string; vruchnuyu: string; tresh: string; tresh_tochno: string; otmena: string; popytki: string };
    tresh: { kolonka: string; prichina: string };
  } | null;
  /**
   * `s722`: перезвон — кто кому звонит, когда, ответственный, причина, просрочка (`perezvon`);
   * отложенная переписка — не статус лида (`pauza`); статус прежней воронки без срока (`bez_sroka`).
   */
  perezvon?: {
    vid: 'perezvon' | 'pauza' | 'bez_sroka';
    zagolovok: string;
    prosrochen?: boolean;
    srok?: string;
    stroki: { podpis: string; znachenie: string }[];
    poyasnenie: string | null;
  } | null;
  /** `s722`: отказ по лиду — что закрывается и что будет с записью (семантика сервера, не догадка экрана). */
  otkaz_opisanie?: { obyekt: string; posledstviya: string; zapis: boolean };
  /** `s722`: лид снят с рабочей доски (статус и история целы) — когда и на каком основании. */
  vne_doski?: { kogda: string; slovo: string } | null;
  deal_references?: (DealReference & { name: string; source_status?: { pipeline_id: number; status_id: number; name: string; color: string | null } | null })[];
  deal_create?: boolean;
  kolonka: { kod: string; slovo: string; cvet: string | null } | null;
  /** `s705`: открыта ли переписка лида; закрытый лид двигает дверь статуса (она сама вернёт его в работу). */
  otkryto?: boolean;
  /** `s705`: выбор статуса рукой — девять колонок доски; пусто — не лид. */
  statusy?: StatusLidaDlyaVybora[];
  prichiny_tresh?: Prichina[];
  /** `s702`: подробное состояние внутри колонки (шаг брони, «Недозвон-2», причина отмены). */
  sostoyanie?: { kod: string; slovo: string; ton: string } | null;
  status_cheloveka: string | null;
  istochnik: { kod: string; slovo: string };
  zapis: ZapisLida | null;
  /** `s705`: запись ведётся в YClients (до дня X); `zhurnal` — журнал кабинета филиала лида. */
  us?: { vedyot: boolean; zhurnal: string | null };
  dveri: {
    zapisat: boolean;
    pozvonit: boolean;
    ne_dozvonilas: boolean;
    perezvonit: boolean;
    peredat: boolean;
    otkaz: boolean;
    v_obzvon: boolean;
  };
  prichiny_otkaza_lida: Array<{ id: number; label: string }>;
  peredat: {
    lyudi: Array<{ id: number; name: string }>;
    prichiny: Prichina[];
  };
  istoriya_vkladki: Array<{ kod: string; slovo: string }>;
}

export type RazborLida = Razbor & { doska: BlokDoski };

export interface IstoriyaOtvet {
  vkladka: string;
  stroki: Array<{ kogda: string; tekst: string; podpis: string | null; ssylka: string | null }>;
  pusto: string | null;
}

export async function fetchIstoriya(id: number, vkladka: string): Promise<IstoriyaOtvet> {
  const { data } = await apiClient.get<IstoriyaOtvet>(`/obrashcheniya/${id}/istoriya`, { params: { vkladka } });

  return data;
}

/** Ход карточки лида: сервер возвращает разбор целиком (закон `s104`). */
async function hodLida(id: number, put: string, telo: Record<string, unknown> = {}, versiyaPlitki?: string | null): Promise<RazborLida> {
  await ensureCsrfCookie();
  // `s722` по контракту `s719`: ход с плитки несёт версию, с которой плитку показали (общий клиент
  // свою версию поверх названной не ставит). Карточка открыта или версии нет — решает общий клиент.
  const { data } = await apiClient.post<RazborLida>(`/obrashcheniya/${id}/${put}`, telo,
    typeof versiyaPlitki === 'string' && versiyaPlitki !== '' ? { headers: { 'X-Versiya': versiyaPlitki } } : undefined);

  return data;
}

export const otkazLida = (id: number, reasonId: number, zametka: string | null) =>
  hodLida(id, 'otkaz', { reason_id: reasonId, zametka: zametka ?? undefined });

export const peredatLid = (id: number, userId: number, prichina: string) =>
  hodLida(id, 'peredat', { user_id: userId, prichina });

export const lidVObzvon = (id: number) => hodLida(id, 'v-obzvon');

export const neDozvonilas = (id: number) => hodLida(id, 'ne-dozvonilas');

export const perezvonitV = (id: number, doKogda: string, zametka?: string | null) =>
  hodLida(id, 'otlozhit', { prichina: 'klientka_perezvonit', do_kogda: doKogda, zametka: zametka ?? undefined });

export const peretashchitLid = (id: number, kolonka: string, telo: Record<string, unknown> = {}, versiyaPlitki?: string | null) =>
  hodLida(id, 'peretashchit', { kolonka, ...telo }, versiyaPlitki);

/**
 * `s705`: ручная смена статуса лида — одна дверь на карточку и доску. `kolonka` — код колонки
 * доски; час, причина отказа или причина ТРЕШа — в `telo`.
 */
export const postavitStatusLida = (id: number, kolonka: string, telo: Record<string, unknown> = {}) =>
  hodLida(id, 'status', { kolonka, ...telo });

/** Двери денег `s632` на записи лида. До дня X ручные двери открыты (отчёт `s632`). */
export async function smsKlientke(bookingId: number, tekst: string, klyuchNamereniya?: string): Promise<{ message?: string }> {
  await ensureCsrfCookie();
  // `s713` по контракту `s711`: одно нажатие — один ключ; повтор того же нажатия вторую СМС не шлёт.
  const { data } = await apiClient.post(`/zapis/${bookingId}/sms-klientke`, { tekst },
    klyuchNamereniya === undefined ? {} : { headers: { 'Idempotency-Key': klyuchNamereniya } });

  return data as { message?: string };
}

/*
 * ⛔⛔ ССЫЛКА ОПЛАТЫ — СНАЧАЛА ПОКАЗ, ПОТОМ ОТПРАВКА (`s713` по контракту `s711`, Д-05 приёмки
 * 05.10.2026: при приёмке кнопка сразу отправила СМС настоящей клиентке).
 * Предпросмотр ничего не шлёт; подтверждение принимает только пропуск показанного, а сервер
 * сверяет, что с момента показа ничего не изменилось.
 */
export interface PredprosmotrOtpravki {
  deystvie: string;
  poluchatel: { customer_id: number | null; imya: string | null; telefon: string | null };
  kanal: { vid: string; slovo: string };
  liniya: { vid: string; otpravitel: string | null; filial: { id: number; nazvanie: string } | null };
  avtor: { id: number; imya: string } | null;
  tekst: string;
  segmentov?: number | null;
  zapis: { id: number; kogda: string | null; kogda_slovo: string | null } | null;
  obrashchenie_id?: number | null;
  oplata: { naznachenie: string | null; summa_rubles: number | null; ssylka: string | null; payment_id?: number | null; deystvuet_do: string | null; schyot?: 'est' | 'net' } | null;
  /** `s720`: счёта по записи ещё нет — в тексте на месте ссылки пометка, отправить нельзя, пока счёт не создан. */
  nuzhen_schyot?: boolean;
  mesto_ssylki?: string | null;
}

export interface OtvetPredprosmotra {
  ok: boolean;
  message?: string;
  kod?: string;
  predprosmotr: PredprosmotrOtpravki;
  /** `null` — счёта ещё нет, подтверждать нечего (`s720`). */
  propusk: string | null;
  deystvitelen_do: string | null;
}

export interface ItogOtpravkiSms {
  ok: boolean;
  message?: string;
  sms_id?: number | null;
  tekst?: string;
  rezultat?: { sostoyanie: string; dostavka: string; slovo: string | null; kogda: string | null; oshibka: unknown } | null;
  namerenie?: { povtor?: boolean; slovo?: string | null } | null;
}

export async function predprosmotrSsylkiOplaty(bookingId: number): Promise<OtvetPredprosmotra> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<OtvetPredprosmotra>(`/zapis/${bookingId}/ssylka-oplaty/predprosmotr`, {});

  return data;
}

/**
 * `s720`: создать счёт — отдельное явное действие. Показ (`predprosmotrSsylkiOplaty`) счёт не
 * выпускает; этот ход выпускает (действующий переиспользуется) и возвращает показ со ссылкой и
 * пропуском. СМС не отправляет.
 */
export async function sozdatSchyotSsylkiOplaty(bookingId: number): Promise<OtvetPredprosmotra> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<OtvetPredprosmotra>(`/zapis/${bookingId}/ssylka-oplaty/schyot`, {});

  return data;
}

export async function podtverditSsylkuOplaty(bookingId: number, propusk: string): Promise<ItogOtpravkiSms> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<ItogOtpravkiSms>(`/zapis/${bookingId}/ssylka-oplaty/podtverdit`, { propusk });

  return data;
}

// ── Доска записей ────────────────────────────────────────────────────────────

export interface KartochkaZapisiNaDoske {
  id: number;
  kogda: string;
  klientka: string | null;
  telefon: string | null;
  customer_id: number | null;
  master: string | null;
  usluga: string | null;
  metki: Metka[];
  /** «25.09 14:00» — в полосу времени плашки (`s635` Д2). */
  kogda_korotko: string | null;
  /** «создана 14.09 (14 дней назад) · визит 01.10 (через 3 дня)» — словами сервера (`s682` П5). */
  daty: string | null;
  /** `s715`: то же короче, для компактной плитки — «создана 2 дня назад · визит завтра»; прежний сервер поля не шлёт. */
  daty_korotko?: string | null;
  /** Строка журнала дня — плашка рисуется тем же компонентом, что в расписании (`s635` Д2). */
  zhurnal: ZapisRow;
}

export interface KolonkaZapisey {
  kod: string;
  slovo: string;
  /** Ключ палитры журнала — краска берётся из `PALITRY` журнала, не своя. */
  palitra: ColorKey;
  svernuta: boolean;
  trevoga: boolean;
  chislo: number;
  kartochki: KartochkaZapisiNaDoske[];
}

/** `s682` П5: «Создана» — назад от сегодня, «Визит» — вперёд; по умолчанию оба «все». */
export const SOZDANA = ['vse', 'segodnya', 'vchera', '3_dnya', 'nedelya', '2_nedeli', 'mesyac', '3_mesyaca'] as const;
export const VIZIT = ['vse', 'segodnya', 'zavtra', '3_dnya', 'nedelya', '2_nedeli', 'mesyac', '3_mesyaca'] as const;
export type FiltrSozdana = (typeof SOZDANA)[number];
export type FiltrVizit = (typeof VIZIT)[number];

export interface DoskaZapiseyFiltr {
  sozdana: FiltrSozdana;
  vizit: FiltrVizit;
  filial_id: number | null;
  staff_id: number | null;
  po_lidam: boolean;
  onlayn: boolean;
  povtornye: boolean;
  predzapisi: boolean;
}

export interface DoskaZapiseyOtvet {
  filtr: DoskaZapiseyFiltr;
  vsego: number;
  kolonki: KolonkaZapisey[];
  filtry: {
    sozdana: Array<{ kod: FiltrSozdana; slovo: string }>;
    vizit: Array<{ kod: FiltrVizit; slovo: string }>;
    filialy: Array<{ id: number; name: string }>;
    mastera: Array<{ id: number; name: string }>;
  };
  predel_obzvona: number;
  words: ScreenWords;
}

export async function fetchDoskaZapisey(f: DoskaZapiseyFiltr): Promise<DoskaZapiseyOtvet> {
  const { data } = await apiClient.get<DoskaZapiseyOtvet>('/doska/zapisi', {
    params: {
      sozdana: f.sozdana === 'vse' ? undefined : f.sozdana,
      vizit: f.vizit === 'vse' ? undefined : f.vizit,
      filial_id: f.filial_id ?? undefined,
      staff_id: f.staff_id ?? undefined,
      po_lidam: f.po_lidam ? 1 : undefined,
      onlayn: f.onlayn ? 1 : undefined,
      povtornye: f.povtornye ? 1 : undefined,
      predzapisi: f.predzapisi ? 1 : undefined,
    },
  });

  return data;
}

/** `s674`: «В обзвон» колонкой доски лидов — руками, без правил (Л-30). */
export type ObzvonLidovItog = {
  postavleno: number;
  uzhe: number;
  bez_nomera: number;
  otkaz: number;
  nashi: number;
  itog: string;
};

export async function obzvonLidov(ids: number[]): Promise<ObzvonLidovItog> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<ObzvonLidovItog>('/doska/lidy/obzvon', { inquiry_ids: ids });

  return data;
}

/** Полная колонка идёт последовательными пакетами по пределу серверной двери. */
export async function obzvonLidovPachkami(
  ids: number[],
  onPachka: (itog: ObzvonLidovItog) => void,
): Promise<void> {
  const unique = [...new Set(ids)];
  for (let offset = 0; offset < unique.length; offset += 200) {
    onPachka(await obzvonLidov(unique.slice(offset, offset + 200)));
  }
}

export async function obzvonitVybrannyh(ids: number[]): Promise<{ postavleno: number; itog: string }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<{ postavleno: number; itog: string }>('/doska/zapisi/obzvon', { booking_ids: ids });

  return data;
}

/** Слово отказа сервера (422 `{message}`) — показывается как есть. */
export function slovoOtkaza(e: unknown): string {
  const otvet = (e as { response?: { data?: { message?: unknown } } }).response?.data?.message;

  return typeof otvet === 'string' && otvet !== '' ? otvet : 'Не получилось.';
}

/**
 * ⛔⛔ «ЗАПИСАТЬ» ИЗ ЛИДА — ОДНИМ ВЫРАЖЕНИЕМ НА КАРТОЧКУ ЛИДА И ОЧЕРЕДЬ (`s635` Д2,
 * `[s635-zapisat]`: работает уже сейчас, пишет в НАШ журнал). Открывается журнал
 * филиала лида; окно новой записи от любого свободного слота встаёт с клиенткой
 * лида и заводит запись дверью лида — обращение закрывается фактом записи.
 * Клиентка и лид едут состоянием перехода: номер в адрес не пишется.
 */
export interface LidDlyaZapisi {
  id: number;
  klientka: { id: number; name: string; phone_display: string } | null;
}

/**
 * ⛔⛔ `s677` «ЗАПИСАТЬ» РЯДОМ С ПЕРЕПИСКОЙ (слово владельца 26.09, вариант «А»):
 * то же выражение для карточки лида, очереди «Обращения» и доски — с лидом и без
 * него. Клиентки нет в базе — едет номер переписки маской: клиентка заведётся по
 * нему при записи дверью лида.
 */
export interface ZapisIzPerepiski {
  lidId: number;
  filialId: number | null;
  klientka: { id: number; name: string; phone_display: string } | null;
  nomer: string | null;
  imya: string | null;
}

export function zapisIzRazbora(razbor: Pick<Razbor, 'id' | 'shapka'>): ZapisIzPerepiski {
  const { shapka } = razbor;

  return {
    lidId: razbor.id,
    filialId: shapka.filial.id,
    klientka: shapka.customer_id !== null
      ? { id: shapka.customer_id, name: shapka.customer_display ?? shapka.telefon ?? '', phone_display: shapka.telefon ?? '' }
      : null,
    nomer: shapka.telefon ?? shapka.nomer_bez_klientki ?? null,
    imya: shapka.customer_display ?? shapka.telefon ?? null,
  };
}

export function putZapisiIzLida(razbor: Pick<Razbor, 'id' | 'shapka'>): { to: string; state: { lidDlyaZapisi: LidDlyaZapisi } } {
  const { shapka } = razbor;
  const klientka = shapka.customer_id !== null
    ? { id: shapka.customer_id, name: shapka.customer_display ?? shapka.telefon ?? '', phone_display: shapka.telefon ?? '' }
    : null;

  return {
    to: shapka.filial.id !== null ? `/zapis/den?filial_id=${shapka.filial.id}` : '/zapis/den',
    state: { lidDlyaZapisi: { id: razbor.id, klientka } },
  };
}

/** Дверь лида «Записать» (`s633`): запись своя, обращение закрывается фактом; в ответе — номер записи. */
export async function zapisatLid(
  lidId: number,
  telo: { filial_id: number; staff_id: number; slot_start: string; booked_procedure_id?: number; customer_id?: number },
): Promise<{ booking_id: number }> {
  await ensureCsrfCookie();
  const { data } = await apiClient.post<{ booking_id: number }>(`/obrashcheniya/${lidId}/zapisat`, telo);

  return data;
}
